import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import {
  useDeleteDdRequest,
  useLinkDdDocument,
  useSetDdRequestRequired,
  useSetDdRequestStatus,
  useUnlinkDdDocument,
} from '@/api/hooks';
import {
  ApiError,
  Button,
  Chip,
  ConfirmDialog,
  ErrorText,
  HelpTip,
  Icon,
  LinkDocumentDialog,
  RowMenu,
  UnlinkDocumentDialog,
  cn,
  formatDateOnly,
  formatDateTime,
  isAccountPlaceholderItem,
  isStatePlaceholderItem,
  itemFulfillment,
  NA_ATTESTATION_KEY,
  NA_ATTESTATION_TEXT,
  placeholderAccountsLine,
  toYmd,
  todayYmd,
  useToast,
  type RowMenuItem,
} from '@dataroom/shared';
import type { DdRequest, RoomDocument } from '@dataroom/shared';
import { DdAccountsDialog } from './DdAccountsPanel';
import { DdStatesDialog } from './DdStatesPanel';

/**
 * One checklist item (redesign slice 4, AI-first). One line by default: the title,
 * a date only when it is the item's own or overdue (the phase's due date is in the
 * phase plan), the status chip and ⋯. Choosing the title opens the rest: detail,
 * linked documents ("Matched automatically"), files we could not read, and the
 * seller's "doesn't apply" record.
 *
 * What only a person can do is on the row itself: an AI match WAITING ON REVIEW
 * shows the suggested document with Confirm (links it) and Not this one (rejects
 * the pair — the API records the rejection, so the AI never suggests or links
 * that document for this item again; the toast offers the document's
 * classification review, where it can be re-filed). Filing by hand is a fallback,
 * so "Link a document…" lives in ⋯, never as a link on every row.
 *
 * Reversible actions (provided/reopen, required/optional) act at once and offer
 * Undo in a toast; Remove has no undo endpoint, so it confirms first. The seller's
 * "doesn't apply" is READ-ONLY here (owner ruling 2026-10-05, ADR-0032 §5).
 */
export function ChecklistItemRow({
  roomId,
  req,
  documents,
  actorLabel,
  statusTip = false,
  onReviewDocument,
  highlighted = false,
  onEdit,
  editForm,
}: {
  roomId: string;
  req: DdRequest;
  documents: RoomDocument[];
  /** Resolve an actor id to a label (email) for the "doesn't apply" record. */
  actorLabel: (actorId: string) => string;
  /** Show the "?" for the status chip (once per phase). */
  statusTip?: boolean;
  /** Open a document's classification review (offered after "Not this one"). */
  onReviewDocument?: (documentId: string) => void;
  /** Just added: brought into view, focused and marked for a moment. */
  highlighted?: boolean;
  /** Open this item's edit form (⋯ → Edit…). */
  onEdit?: () => void;
  /** The open edit form, shown in place of the row. */
  editForm?: ReactNode;
}) {
  const setStatus = useSetDdRequestStatus(roomId);
  const deleteReq = useDeleteDdRequest(roomId);
  const setRequired = useSetDdRequestRequired(roomId);
  const link = useLinkDdDocument(roomId);
  const unlink = useUnlinkDdDocument(roomId);
  const toast = useToast();
  const detailsId = useId();
  const titleRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!highlighted) return;
    titleRef.current?.scrollIntoView?.({ block: 'center' });
    titleRef.current?.focus({ preventScroll: true });
  }, [highlighted]);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [linking, setLinking] = useState(false);
  // The linked document whose unlink is being confirmed.
  const [unlinking, setUnlinking] = useState<string | null>(null);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [statesOpen, setStatesOpen] = useState(false);
  // "One category per account" (ADR-0037): answered by the roster, not a document.
  const placeholder = isAccountPlaceholderItem(req);
  // "One category per state" (issue #157): answered by choosing states.
  const statePlaceholder = isStatePlaceholderItem(req);

  const done = req.status === 'completed';
  const state = itemFulfillment(req);
  // Unresolved = neither covered by a document NOR attested not applicable —
  // required or optional alike (ADR-0032).
  const unresolved = state === 'missing' || state === 'waiting_on_review';
  // Calendar-date comparison in the viewer's LOCAL zone (both sides YYYY-MM-DD).
  const dueYmd = toYmd(req.dueDate);
  const overdue = unresolved && dueYmd !== null && dueYmd < todayYmd();
  // The phase's due date is in the phase plan: a row shows a date only when it is
  // its own, or when it has passed.
  const showDue = req.dueDate !== null && (!req.dueDateInherited || overdue);
  const attestation = req.notApplicable ? (req.notApplicableAttestation ?? null) : null;
  const docName = (id: string) =>
    documents.find((d) => d.id === id)?.name ?? 'A document in the data room';
  const suggested = (req.reviewDocumentIds ?? []).filter(
    (id) => !req.linkedDocumentIds.includes(id),
  );
  const unreadable = new Set(req.unreadableDocumentIds ?? []);
  const title = `“${req.title}”`;

  /** Link the chosen documents one by one (the API takes one per call). */
  async function linkDocuments(documentIds: string[]) {
    for (const documentId of documentIds) {
      await link.mutateAsync({ requestId: req.id, documentId });
    }
    toast({
      message:
        documentIds.length === 1
          ? `Linked “${docName(documentIds[0]!)}” to ${title}`
          : `Linked ${documentIds.length} documents to ${title}`,
    });
  }

  const actionError =
    setStatus.error instanceof ApiError
      ? setStatus.error.message
      : deleteReq.error instanceof ApiError
        ? deleteReq.error.message
        : setRequired.error instanceof ApiError
          ? setRequired.error.message
          : link.error instanceof ApiError
            ? link.error.message
            : // The unlink dialog shows its own error while it is open.
              !unlinking && unlink.error instanceof ApiError
              ? unlink.error.message
              : null;

  /** "Not this one": reject the AI's match. Unlinking the pair records the
   * rejection (and drops the suggestion), so the after-filing job never brings it
   * back; the document stays in the room. */
  const rejectMatch = (documentId: string) =>
    unlink.mutate(
      { requestId: req.id, documentId },
      {
        onSuccess: () =>
          toast({
            message: `“${docName(documentId)}” won’t be suggested for ${title} again`,
            ...(onReviewDocument
              ? { action: { label: 'Review its filing', onSelect: () => onReviewDocument(documentId) } }
              : {}),
          }),
      },
    );

  const markStatus = (status: 'open' | 'completed', undo: boolean) =>
    setStatus.mutate(
      { requestId: req.id, body: { status } },
      undo
        ? {}
        : {
            onSuccess: () =>
              toast({
                message:
                  status === 'completed' ? `Marked ${title} received` : `Reopened ${title}`,
                action: {
                  label: 'Undo',
                  onSelect: () => markStatus(status === 'completed' ? 'open' : 'completed', true),
                },
              }),
          },
    );
  const markRequired = (required: boolean, undo: boolean) =>
    setRequired.mutate(
      { requestId: req.id, body: { required } },
      undo
        ? {}
        : {
            onSuccess: () =>
              toast({
                message: required ? `Marked ${title} required` : `Marked ${title} optional`,
                action: { label: 'Undo', onSelect: () => markRequired(!required, true) },
              }),
          },
    );
  const actions: RowMenuItem[] = [
    // A roster placeholder is answered by its accounts or states, not edited by hand.
    ...(onEdit && !placeholder && !statePlaceholder
      ? [{ label: 'Edit…', icon: 'pen' as const, onSelect: onEdit }]
      : []),
    ...(placeholder || statePlaceholder
      ? []
      : [{ label: 'Link a document…', icon: 'file' as const, onSelect: () => setLinking(true) }]),
    done
      ? {
          label: 'Reopen (clear “received”)',
          icon: 'refresh',
          onSelect: () => markStatus('open', false),
        }
      : // The ADR-0032 override: counted as received though no document is linked.
        {
          label: 'Mark received (no document)',
          icon: 'check',
          onSelect: () => markStatus('completed', false),
        },
    req.required
      ? {
          label: 'Mark optional',
          icon: 'notApplicable',
          onSelect: () => markRequired(false, false),
        }
      : { label: 'Mark required', icon: 'check', onSelect: () => markRequired(true, false) },
    { label: 'Remove', icon: 'trash', danger: true, onSelect: () => setConfirmRemove(true) },
  ];

  if (editForm) return <li className="px-3 py-2">{editForm}</li>;

  return (
    <li
      className={cn(
        'px-3 py-2 transition-colors',
        highlighted && 'bg-primary-soft ring-2 ring-inset ring-primary',
      )}
    >
      {/* One line on a wide row; on a narrow one the status and ⋯ wrap below the title. */}
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        <button
          ref={titleRef}
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => setOpen((o) => !o)}
          className="flex min-w-[min(100%,14rem)] flex-1 items-start gap-1.5 rounded-md text-left hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon
            name="chevronRight"
            size={14}
            className={cn('mt-0.5 shrink-0 text-ink-3 transition-transform', open && 'rotate-90')}
          />
          <span className="min-w-0 break-words text-sm font-semibold">{req.title}</span>
        </button>
        <span className="ml-auto flex shrink-0 items-center gap-2">
          {showDue ? (
            <span
              className={cn(
                'whitespace-nowrap text-xs',
                overdue ? 'font-semibold text-danger' : 'text-ink-3',
              )}
            >
              {overdue ? 'Overdue · ' : 'Due '}
              {formatDateOnly(req.dueDate)}
            </span>
          ) : null}
          <ItemChip req={req} />
          {statusTip ? <HelpTip id="admin.checklist.item-status" /> : null}
          <RowMenu label={req.title} items={actions} />
        </span>
      </div>

      {/* Facts that need nobody to open the row: optional, unreadable, the roster. */}
      {!req.required || unreadable.size > 0 ? (
        <p className="ml-5 mt-0.5 text-xs text-ink-3">
          {!req.required ? 'Optional — still needs a document or a “doesn’t apply”' : null}
          {!req.required && unreadable.size > 0 ? ' · ' : null}
          {unreadable.size > 0 ? (
            <span className="font-medium text-danger">
              {unreadable.size === 1
                ? 'We could not read 1 of its files'
                : `We could not read ${unreadable.size} of its files`}
            </span>
          ) : null}
        </p>
      ) : null}
      {placeholder || statePlaceholder ? (
        <div className="ml-5 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <span className="text-ink-2">{placeholderAccountsLine(req)}</span>
          <button
            type="button"
            className="font-medium text-primary hover:underline"
            onClick={() => (placeholder ? setAccountsOpen(true) : setStatesOpen(true))}
          >
            {placeholder ? 'Manage accounts' : 'Manage states'}
          </button>
        </div>
      ) : null}

      {/* The one thing only a person can do: confirm (or not) what the AI matched. */}
      {suggested.length > 0 ? (
        <ul className="ml-5 mt-1.5 space-y-1.5" aria-label={`Suggested for ${req.title}`}>
          {suggested.map((id) => (
            <li
              key={id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-xs"
            >
              <span className="flex min-w-[min(100%,12rem)] flex-1 items-center gap-1.5">
                <Icon name="spark" size={14} className="shrink-0 text-ink-3" />
                <span className="min-w-0 break-words">
                  <span className="text-ink-2">The AI matched </span>
                  <span className="font-medium text-ink">{docName(id)}</span>
                </span>
              </span>
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                <Button
                  size="sm"
                  disabled={link.isPending || unlink.isPending}
                  onClick={() =>
                    link.mutate(
                      { requestId: req.id, documentId: id },
                      { onSuccess: () => toast({ message: `Linked “${docName(id)}” to ${title}` }) },
                    )
                  }
                >
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={link.isPending || unlink.isPending}
                  onClick={() => {
                    unlink.reset();
                    rejectMatch(id);
                  }}
                >
                  Not this one
                </Button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {open ? (
        <div id={detailsId} className="ml-5 mt-1.5 space-y-1.5 text-xs">
          {req.detail ? <p className="text-ink-2">{req.detail}</p> : null}
          {/* The line above shows the item's own (or a passed) date; the phase's
              is said here, where someone looking for it has opened the item. */}
          {!showDue ? (
            <p className="text-ink-3">
              {req.dueDate ? `Due ${formatDateOnly(req.dueDate)} (phase deadline)` : 'No deadline'}
            </p>
          ) : null}
          {req.linkedDocumentIds.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label={`Documents for ${req.title}`}>
              {req.linkedDocumentIds.map((id) => (
                <li
                  key={id}
                  className="inline-flex h-7 max-w-full items-center gap-1.5 rounded-md border bg-surface px-2 text-xs"
                >
                  <Icon name="file" size={14} className="text-ink-3" />
                  <span className="truncate">{docName(id)}</span>
                  {req.autoLinkedDocumentIds?.includes(id) ? (
                    <span className="shrink-0 text-ink-3">· Matched automatically</span>
                  ) : null}
                  {unreadable.has(id) ? (
                    <span className="shrink-0 font-medium text-danger">· Could not be read</span>
                  ) : null}
                  <button
                    type="button"
                    className="-mr-1 inline-flex shrink-0 items-center rounded px-1 text-ink-3 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Unlink “${docName(id)}”`}
                    title="Unlink"
                    onClick={() => {
                      unlink.reset();
                      setUnlinking(id);
                    }}
                  >
                    <Icon name="close" size={12} />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-3">No document linked yet.</p>
          )}
          {unreadable.size > 0 ? (
            <p className="text-ink-2">
              A file we could not read does not answer the item; the seller is asked for a
              readable copy.
            </p>
          ) : null}
          {attestation ? (
            <div className="text-ink-2">
              <p>
                The seller confirmed this doesn’t apply to their company
                {attestation.attestedBy ? ` · ${actorLabel(attestation.attestedBy)}` : ''} ·{' '}
                {formatDateTime(attestation.attestedAt, attestation.attestedAt)}
              </p>
              <blockquote className="mt-0.5 border-l-2 border-line pl-2">
                {attestation.statementKey === NA_ATTESTATION_KEY
                  ? `“${NA_ATTESTATION_TEXT}”`
                  : `Statement ${attestation.statementKey}`}
              </blockquote>
            </div>
          ) : null}
        </div>
      ) : null}
      {actionError ? (
        <p className="ml-5 mt-1 text-xs text-destructive" role="alert">
          {actionError}
        </p>
      ) : null}

      {linking ? (
        <LinkDocumentDialog
          item={req}
          documents={documents}
          onLink={linkDocuments}
          onClose={() => setLinking(false)}
        />
      ) : null}
      {accountsOpen ? (
        <DdAccountsDialog
          roomId={roomId}
          placeholderTagId={req.categoryTagId}
          onClose={() => setAccountsOpen(false)}
        />
      ) : null}
      {statesOpen ? <DdStatesDialog roomId={roomId} onClose={() => setStatesOpen(false)} /> : null}
      {unlinking ? (
        <UnlinkDocumentDialog
          itemTitle={req.title}
          documentName={docName(unlinking)}
          pending={unlink.isPending}
          error={<ErrorText error={unlink.error} />}
          onCancel={() => setUnlinking(null)}
          onConfirm={() =>
            unlink.mutate(
              { requestId: req.id, documentId: unlinking },
              { onSuccess: () => setUnlinking(null) },
            )
          }
        />
      ) : null}
      {confirmRemove ? (
        <ConfirmDialog
          danger
          title={`Remove ${title} from the checklist?`}
          confirmLabel="Remove item"
          pending={deleteReq.isPending}
          onCancel={() => setConfirmRemove(false)}
          onConfirm={() =>
            deleteReq.mutate(req.id, {
              onSettled: () => setConfirmRemove(false),
              onSuccess: () => toast({ message: `Removed ${title}` }),
            })
          }
        >
          <p>
            The seller no longer sees it. Any documents already provided for it stay in the data
            room. This can’t be undone — add the item again if you need it back.
          </p>
        </ConfirmDialog>
      ) : null}
    </li>
  );
}

/** One checklist-item status (DataRoom UI checklist chips) — the server's
 * fulfillment state (ADR-0032). */
function ItemChip({ req }: { req: DdRequest }) {
  switch (itemFulfillment(req)) {
    case 'covered':
      return (
        <Chip tone="filed" icon="check">
          Covered
        </Chip>
      );
    case 'not_applicable':
      return (
        <Chip tone="na" icon="notApplicable">
          Doesn’t apply
        </Chip>
      );
    case 'waiting_on_review':
      return (
        <Chip tone="processing" icon="eye">
          Waiting on review
        </Chip>
      );
    default:
      return (
        <Chip tone="check" icon="alert">
          Missing
        </Chip>
      );
  }
}
